import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "./axios";

/* ---------------------------------------------------------------- *
 * reads
 * ---------------------------------------------------------------- */

/** Every project the viewer may see, with its requirement counts per state. */
export function useTrackerPortfolio() {
  return useQuery({
    queryKey: ["trackerPortfolio"],
    queryFn: () => api.get("/tracker/portfolio").then((r) => r.data),
  });
}

/** Requirements for one project, optionally filtered to a lifecycle state. */
export function useProjectRequirements(projectId, state) {
  return useQuery({
    queryKey: ["trackerRequirements", projectId, state || "all"],
    queryFn: () =>
      api
        .get(`/tracker/projects/${encodeURIComponent(projectId)}/requirements`, {
          params: state && state !== "all" ? { state } : undefined,
        })
        .then((r) => r.data),
    enabled: !!projectId,
  });
}

/** Stages waiting on me, across every project I can see. */
export function useMyStages() {
  return useQuery({
    queryKey: ["trackerMyStages"],
    queryFn: () => api.get("/tracker/my-stages").then((r) => r.data.stages),
  });
}

/** Per-department stage labels and standard ETAs in days. */
export function useTrackerConfig() {
  return useQuery({
    queryKey: ["trackerConfig"],
    queryFn: () => api.get("/tracker/config").then((r) => r.data),
  });
}

export function useSaveTrackerConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (config) => api.put("/tracker/config", { config }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trackerConfig"] });
      qc.invalidateQueries({ queryKey: ["trackerRequirements"] });
    },
  });
}

/** Project members, sorted so the roles that suit this stage come first. */
export function useEligibleOwners(projectId, stageCode) {
  return useQuery({
    queryKey: ["trackerOwners", projectId, stageCode],
    queryFn: () =>
      api
        .get(`/tracker/projects/${encodeURIComponent(projectId)}/eligible-owners`, {
          params: stageCode ? { stageCode } : undefined,
        })
        .then((r) => r.data.members),
    enabled: !!projectId,
  });
}

/* ---------------------------------------------------------------- *
 * writes
 * ---------------------------------------------------------------- */

/** Everything that changes a requirement invalidates the same two views. */
function useTrackerMutation(mutationFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trackerRequirements"] });
      qc.invalidateQueries({ queryKey: ["trackerPortfolio"] });
      qc.invalidateQueries({ queryKey: ["trackerMyStages"] });
    },
  });
}

export function useRaiseRequirement() {
  return useTrackerMutation((body) => api.post("/tracker/requirements", body).then((r) => r.data));
}

export function useUpdateRequirement() {
  return useTrackerMutation(({ id, ...body }) =>
    api.put(`/tracker/requirements/${id}`, body).then((r) => r.data)
  );
}

/** Record a checklist point that was promoted into a Daily Notebook entry. */
export function linkChecklistItemToEntry({ id, itemId, entryId }) {
  return api
    .put(`/tracker/requirements/${id}/checklist/${itemId}/link-entry`, { entryId })
    .then((r) => r.data);
}

export function useAcceptStage() {
  return useTrackerMutation(({ id, stageCode, ...body }) =>
    api.post(`/tracker/requirements/${id}/stages/${stageCode}/accept`, body).then((r) => r.data)
  );
}

/** Admin/superadmin/manager only — starts a pending stage without the owner's accept. */
export function useStartStageNow() {
  return useTrackerMutation(({ id, stageCode }) =>
    api.post(`/tracker/requirements/${id}/stages/${stageCode}/start-now`).then((r) => r.data)
  );
}

/** Admin/superadmin/manager only - move to any later stage, marking the ones passed as completed. */
export function useMoveToStage() {
  return useTrackerMutation(({ id, ...body }) =>
    api.post(`/tracker/requirements/${id}/move-to-stage`, body).then((r) => r.data)
  );
}

export function useCompleteStage() {
  return useTrackerMutation(({ id, stageCode, ...body }) =>
    api.post(`/tracker/requirements/${id}/stages/${stageCode}/complete`, body).then((r) => r.data)
  );
}

export function useBlockStage() {
  return useTrackerMutation(({ id, stageCode, ...body }) =>
    api.post(`/tracker/requirements/${id}/stages/${stageCode}/block`, body).then((r) => r.data)
  );
}

export function useUnblockStage() {
  return useTrackerMutation(({ id, stageCode, ...body }) =>
    api.post(`/tracker/requirements/${id}/stages/${stageCode}/unblock`, body).then((r) => r.data)
  );
}

export function useRejectStage() {
  return useTrackerMutation(({ id, stageCode, ...body }) =>
    api.post(`/tracker/requirements/${id}/stages/${stageCode}/reject`, body).then((r) => r.data)
  );
}

export function useReassignStage() {
  return useTrackerMutation(({ id, stageCode, ...body }) =>
    api.put(`/tracker/requirements/${id}/stages/${stageCode}/owner`, body).then((r) => r.data)
  );
}

export function usePostStageUpdate() {
  return useTrackerMutation(({ id, stageCode, ...body }) =>
    api.post(`/tracker/requirements/${id}/stages/${stageCode}/updates`, body).then((r) => r.data)
  );
}

export function useUpdateRollout() {
  return useTrackerMutation(({ id, ...body }) =>
    api.put(`/tracker/requirements/${id}/rollout`, body).then((r) => r.data)
  );
}

export function useToggleChecklistItem() {
  return useTrackerMutation(({ id, itemId, done }) =>
    api.put(`/tracker/requirements/${id}/checklist/${itemId}`, { done }).then((r) => r.data)
  );
}
